import { t } from '@bia-auction/shared-i18n';
import {
  companySummarySchema,
  paginatedSchema,
  saleSummarySchema,
  vehicleSummarySchema,
  VEHICLE_FILTER_NONE,
} from '@bia-auction/shared-schema';
import {
  Button,
  cn,
  EmptyState,
  Input,
  Label,
  PageHeader,
  Select,
  SkeletonRows,
  StatusBadge,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@bia-auction/ui';
import { useQuery } from '@tanstack/react-query';
import { Link, useNavigate, useSearch } from '@tanstack/react-router';
import { Car, Plus, Search } from 'lucide-react';
import { useState } from 'react';
import { z } from 'zod';
import { ErrorMsg } from '../components/Feedback';
import { Pagination } from '../components/Pagination';
import { VehicleThumb } from '../components/VehicleThumb';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { formatCount, formatKurus } from '../lib/format';
import { can } from '../lib/roles';
import { useDebounced } from '../lib/useDebounced';
import { vehicleName } from '../lib/vehicle-labels';

/**
 * The vehicle pool (M11, redesign-vehicle.md §1) — the entry point that never existed: until
 * this milestone a vehicle could only reach the system through the `CLIENT` ingestion API.
 *
 * Deliberately a mirror of `SalesListPage`: same debounced `q`, same `{ items, total }`
 * envelope, same `Pagination`. An operator who knows one list knows this one.
 *
 * The filter bar (08.2026 stakeholder feedback) answers the questions a plate search cannot:
 * *which cars are in this auction*, *what is still free to list*, *whose stock is this*, *how
 * old*. `q` already covers brand/series/model text, so there is no separate brand box. The
 * three pickers live in the address (`/vehicles?saleId=…`), so "the cars in this auction" is a
 * link you can send; the search box and year range are local to the session.
 */

const PAGE = 25;
const vehiclesPageSchema = paginatedSchema(vehicleSummarySchema);
const salesPageSchema = paginatedSchema(saleSummarySchema);

/**
 * How many auctions the picker offers — the API's own `limit` ceiling, in one call. The list is
 * `startDate DESC`, so these are the newest, and a deployment past ~100 auctions loses the
 * oldest from the dropdown. It does **not** lose them from the filter: a `?saleId=` outside the
 * page still filters, and {@link selectedSaleOption} keeps it selectable rather than leaving the
 * control blank while the table is filtered. A searchable lookup (`/admin/sales?q=`) is the
 * escalation when the dropdown itself stops being enough.
 */
const SALE_OPTIONS = 100;

/** The three `<select>`s, whose state is the address itself. */
interface PickedFilters {
  saleId: string;
  companyId: string;
  status: '' | 'available' | 'committed';
}

/** The boxes an operator types into: local to the session and debounced, never addressable. */
interface TypedFilters {
  /** Free text over code/plate/brand/series/model. */
  q: string;
  yearMin: string;
  yearMax: string;
}

type Filters = PickedFilters & TypedFilters;

/** The two filters the API validates as a range, and the range it accepts (`vehicleListQuerySchema`). */
const YEAR_KEYS = ['yearMin', 'yearMax'] as const;

/** Whether a typed year is one the API would accept — "2" and "201" are still on their way. */
function isYear(value: string): boolean {
  return /^\d{4}$/.test(value) && Number(value) >= 1900 && Number(value) <= 2999;
}

/**
 * A year that is set but not applied. Judged on the **settled** value, never the live one: a
 * held-back year is invisible in the results, so every one of them has to be reported — `201`
 * as much as `1800` — but reporting it per keystroke would flash an error at someone halfway
 * through typing `2015`. The debounce draws that line for us.
 */
function isUnusableYear(value: string): boolean {
  const settled = value.trim();
  return settled !== '' && !isYear(settled);
}

/** The route's `validateSearch` output (`router.tsx`): the pickers, absent rather than empty. */
type VehicleSearch = { [K in keyof PickedFilters]?: Exclude<PickedFilters[K], ''> };

export function VehiclesListPage() {
  const { state } = useAuth();
  const navigate = useNavigate();

  /**
   * **The three pickers live in the URL and nowhere else.** Selecting one navigates; the
   * controls render from `useSearch`. There is no second copy to keep in step, so an incoming
   * link, the sidebar's own "Araçlar", the Back button and this page's own writes are all the
   * same event — which is the point. An earlier revision mirrored a local copy into the address
   * and had to tell its own writes apart from everyone else's; every ordering of two
   * navigations crossing was a bug, and fixing one produced its mirror image.
   *
   * The typed boxes are the other half of that line and stay **entirely** out of the address —
   * not written, not read. Feeding a controlled input from an async router commit drops
   * characters, and a box the URL can carry but never track is worse than one it ignores: the
   * address ends up describing a search that has since moved on, or needing a sync leg that can
   * overwrite what someone is typing. So the rule is flat, and the handbook says it: the
   * auction, status and firm are what a link carries — the ones an operator shares anyway.
   */
  const search = useSearch({ strict: false }) as Partial<PickedFilters>;
  const picked: PickedFilters = {
    saleId: search.saleId ?? '',
    companyId: search.companyId ?? '',
    status: search.status ?? '',
  };
  const [typed, setTyped] = useState<TypedFilters>({ q: '', yearMin: '', yearMax: '' });

  /**
   * Paging belongs to a filter set, so it is *stored with* one and read back only for the set
   * on screen. Page 3 of the old filter is not page 3 of the new one — it asks for rows past
   * the end and prints an impossible range ("51–10 / 10").
   *
   * Derived during render rather than reset by an effect: the address can change the pickers
   * without going through `pick` (the sidebar's "Araçlar", Back), and an effect only runs
   * *after* the render that already built the query — so the stale offset would still go out
   * once, against the new filters, before being corrected.
   */
  const [paging, setPaging] = useState({ key: '', offset: 0 });
  const pickedKey = `${picked.saleId}\u0000${picked.companyId}\u0000${picked.status}`;
  const offset = paging.key === pickedKey ? paging.offset : 0;
  const goToPage = (next: number) => setPaging({ key: pickedKey, offset: next });
  const debounced = useDebounced(typed);
  const filters: Filters = { ...picked, ...typed };

  // An inverted range (2020–2010) matches nothing. Both bounds are held back together —
  // applying one half would answer a question nobody asked — and the bar says why below.
  const sendYears =
    isYear(debounced.yearMin.trim()) && isYear(debounced.yearMax.trim())
      ? Number(debounced.yearMin) <= Number(debounced.yearMax)
      : true;

  const qs = new URLSearchParams({ limit: String(PAGE), offset: String(offset) });
  for (const [key, raw] of Object.entries({ ...picked, ...debounced })) {
    const value = raw.trim();
    // A year is typed a digit at a time, and the debounce is happy to send "2" — which the API
    // rejects (min 1900), replacing the table with an error while the operator is mid-word.
    // Hold it back; the box still shows what was typed, and the bar reports it once settled.
    if (!value) continue;
    if (YEAR_KEYS.includes(key as never) && (!isYear(value) || !sendYears)) continue;
    qs.set(key, value);
  }

  const list = useQuery({
    queryKey: ['vehicles', qs.toString()],
    queryFn: () => api(`/admin/vehicles?${qs.toString()}`, {}, vehiclesPageSchema),
    // Every keystroke and every filter change is a new query key. Without this the table is
    // torn down and replaced by the skeleton on each one — the page flashing at the operator
    // while they type. The previous rows stay put, dimmed, until the new ones land.
    placeholderData: (previous) => previous,
  });

  // The auction picker. Every role that may read the pool may also list sales (firm-scoped),
  // so this needs no permission gate — unlike the firm picker below, which is CompanyAdmin.
  const sales = useQuery({
    queryKey: ['sales', 'filterOptions'],
    queryFn: () => api(`/admin/sales?limit=${SALE_OPTIONS}`, {}, salesPageSchema),
    staleTime: 60_000,
  });
  const canFilterByFirm = can(state.roles, 'companyAdmin');
  const firms = useQuery({
    queryKey: ['companies'],
    queryFn: () => api('/admin/companies', {}, z.array(companySummarySchema)),
    staleTime: 5 * 60_000, // small, slowly-changing directory (shared with CompanyPicker)
    enabled: canFilterByFirm,
  });

  // The selected auction when it is not among the offered options (see `SALE_OPTIONS`): named
  // from a row it is filtering — every row on the page shares that auction, and `committedSale`
  // carries its title for anyone whose sale scope reaches it — else shown as its bare id, which
  // is at least honest about what is filtering the table.
  const offered = sales.data?.items ?? [];
  const chosen = filters.saleId;
  const selectedSaleOption =
    chosen && chosen !== VEHICLE_FILTER_NONE && !offered.some((s) => s.id === chosen)
      ? {
          id: chosen,
          label:
            list.data?.items.find((v) => v.committedSale?.id === chosen)?.committedSale?.title ??
            chosen,
        }
      : null;

  /** A picker: the address is the state, so this navigates rather than setting anything. */
  const pick = (key: keyof PickedFilters) => (e: React.ChangeEvent<HTMLSelectElement>) => {
    const next: PickedFilters = { ...picked, [key]: e.target.value };
    void navigate({
      to: '/vehicles',
      // The pickers are the whole of the address, so this writes exactly them. A `?q=` the
      // page was opened with is **dropped**, not carried forward: the box has been typing
      // locally since arrival, and leaving the arrival value in the URL would have the address
      // describe a search that is no longer running.
      search: {
        saleId: next.saleId || undefined,
        companyId: next.companyId || undefined,
        status: next.status || undefined,
      } satisfies VehicleSearch,
      // `replace`, so refining a filter is not a history entry — Back leaves the pool rather
      // than undoing one select at a time.
      replace: true,
    });
    // No paging reset here: `offset` is derived from the filter set it was stored against, so
    // a picker change — whoever made it — reads back as page one.
  };

  /** A typed box: local and debounced, never written to the address (see above). */
  const set = (key: keyof TypedFilters) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setTyped((f) => ({ ...f, [key]: e.target.value }));
    goToPage(0); // a filter change restarts paging
  };
  const activeCount = Object.values(filters).filter(Boolean).length;
  const filtered = activeCount > 0;
  // Two ways a year range can be unusable, and they need different words: a bound the API
  // won't take, or two good bounds in the wrong order — the second reads as "no vehicles"
  // rather than "backwards filter" unless it is named.
  const invertedYears =
    isYear(debounced.yearMin.trim()) &&
    isYear(debounced.yearMax.trim()) &&
    Number(debounced.yearMin) > Number(debounced.yearMax);
  const badYear = YEAR_KEYS.some((key) => isUnusableYear(debounced[key])) || invertedYears;
  const yearMessage = invertedYears
    ? t('vehicles.filter.yearRangeInvalid')
    : t('vehicles.filter.yearInvalid');

  const newVehicleButton = can(state.roles, 'vehicleEdit') && (
    <Button asChild size="sm">
      <Link to="/vehicles/new">
        <Plus className="h-4 w-4" /> {t('vehicles.new')}
      </Link>
    </Button>
  );

  const clearFilters = () => {
    setTyped({ q: '', yearMin: '', yearMax: '' });
    void navigate({ to: '/vehicles', search: {}, replace: true });
    goToPage(0);
  };

  return (
    <div className="space-y-4">
      <PageHeader title={t('vehicles.title')}>{newVehicleButton}</PageHeader>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div className="grid flex-1 grid-cols-2 gap-3 md:grid-cols-3 xl:grid-cols-5">
          <div className="col-span-2 space-y-1 md:col-span-1">
            <Label htmlFor="v-search">{t('vehicles.filter.search')}</Label>
            <div className="relative">
              <Search aria-hidden className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                id="v-search"
                className="pl-8"
                placeholder={t('vehicles.search.placeholder')}
                value={filters.q}
                onChange={set('q')}
              />
            </div>
          </div>

          <div className="space-y-1">
            <Label htmlFor="v-sale">{t('vehicles.filter.auction')}</Label>
            <Select id="v-sale" value={filters.saleId} onChange={pick('saleId')}>
              <option value="">{t('common.all')}</option>
              <option value={VEHICLE_FILTER_NONE}>{t('vehicles.filter.auction.none')}</option>
              {/* An auction older than the newest `SALE_OPTIONS` — arrived at by link — has no
                  option of its own, and a `<select>` whose value matches nothing renders blank
                  while the table below is very much filtered. Name it from the rows it is
                  filtering, falling back to the id. */}
              {selectedSaleOption && (
                <option value={selectedSaleOption.id}>{selectedSaleOption.label}</option>
              )}
              {(sales.data?.items ?? []).map((s) => (
                <option key={s.id} value={s.id}>
                  {s.code} — {s.title ?? t('sales.untitled')}
                </option>
              ))}
            </Select>
          </div>

          <div className="space-y-1">
            <Label htmlFor="v-status">{t('vehicles.filter.status')}</Label>
            <Select id="v-status" value={filters.status} onChange={pick('status')}>
              <option value="">{t('common.all')}</option>
              <option value="available">{t('vehicles.status.available')}</option>
              <option value="committed">{t('vehicles.status.committed')}</option>
            </Select>
          </div>

          {canFilterByFirm && (
            <div className="space-y-1">
              <Label htmlFor="v-firm">{t('vehicles.filter.firm')}</Label>
              <Select id="v-firm" value={filters.companyId} onChange={pick('companyId')}>
                <option value="">{t('common.all')}</option>
                <option value={VEHICLE_FILTER_NONE}>{t('vehicles.filter.firm.none')}</option>
                {(firms.data ?? []).map((f) => (
                  <option key={f.id} value={f.id}>
                    {f.name}
                  </option>
                ))}
              </Select>
            </div>
          )}

          <div className="space-y-1">
            <Label htmlFor="v-year-min">{t('vehicles.filter.year')}</Label>
            <div className="flex items-center gap-1">
              <Input
                id="v-year-min"
                type="number"
                inputMode="numeric"
                min={1900}
                max={2999}
                aria-label={t('vehicles.filter.yearMin')}
                aria-invalid={isUnusableYear(debounced.yearMin) || invertedYears || undefined}
                aria-describedby={badYear ? 'v-year-hint' : undefined}
                className="tabular-nums"
                value={filters.yearMin}
                onChange={set('yearMin')}
              />
              <span aria-hidden className="text-muted-foreground">
                –
              </span>
              <Input
                id="v-year-max"
                type="number"
                inputMode="numeric"
                min={1900}
                max={2999}
                aria-label={t('vehicles.filter.yearMax')}
                aria-invalid={isUnusableYear(debounced.yearMax) || invertedYears || undefined}
                aria-describedby={badYear ? 'v-year-hint' : undefined}
                className="tabular-nums"
                value={filters.yearMax}
                onChange={set('yearMax')}
              />
            </div>
            {/* A year the API cannot take is held out of the request, which on its own would
                leave the bar claiming a filter the results do not have. Say so — in words, not
                by colour alone (docs/15 §2). `min`/`max` are the browser's affordance; they do
                not stop a typed or deep-linked value. */}
            {badYear && (
              <p id="v-year-hint" className="text-xs text-destructive">
                {yearMessage}
              </p>
            )}
          </div>
        </div>

        <div className="flex items-center gap-2 pb-0.5">
          {activeCount > 0 && (
            <span className="text-sm text-muted-foreground">
              {t('vehicles.filter.active', { n: String(activeCount) })}
            </span>
          )}
          {filtered && (
            <Button variant="ghost" size="sm" onClick={clearFilters}>
              {t('vehicles.filter.clear')}
            </Button>
          )}
          {list.data && (
            <span className="text-sm text-muted-foreground tabular-nums">
              {t('vehicles.count', { n: formatCount(list.data.total) })}
            </span>
          )}
        </div>
      </div>

      {list.isLoading ? (
        <SkeletonRows rows={6} />
      ) : list.isError ? (
        <ErrorMsg error={list.error} />
      ) : list.data && list.data.items.length > 0 ? (
        <Table
          aria-busy={list.isFetching}
          className={cn('transition-opacity', list.isFetching && 'opacity-60')}
        >
          <TableHeader>
            <TableRow>
              <TableHead className="w-20">{t('vehicles.col.photo')}</TableHead>
              <TableHead>{t('vehicles.col.plate')}</TableHead>
              <TableHead>{t('vehicles.col.vehicle')}</TableHead>
              <TableHead>{t('vehicles.col.yearKm')}</TableHead>
              <TableHead>{t('vehicles.col.price')}</TableHead>
              <TableHead>{t('vehicles.col.status')}</TableHead>
              <TableHead>{t('vehicles.col.auction')}</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {list.data.items.map((v) => (
              <TableRow key={v.id}>
                <TableCell>
                  <VehicleThumb url={v.coverUrl} plate={v.plate} className="h-12 w-16" />
                </TableCell>
                <TableCell className="font-mono text-xs tabular-nums">
                  <Link
                    to="/vehicles/$vehicleId"
                    params={{ vehicleId: v.id }}
                    className="text-tx-brand hover:underline"
                  >
                    {v.plate}
                  </Link>
                </TableCell>
                <TableCell>{vehicleName(v)}</TableCell>
                <TableCell className="tabular-nums text-muted-foreground">
                  {[v.year, v.km !== null ? `${formatCount(v.km)} km` : null]
                    .filter(Boolean)
                    .join(' · ') || t('common.none')}
                </TableCell>
                <TableCell className="tabular-nums">
                  {v.startingPrice !== null ? formatKurus(v.startingPrice, v.currency) : t('common.none')}
                </TableCell>
                <TableCell>
                  {/* `committed` is the field that answers "why can't I add this car to a
                      sale" — it is the only status a vehicle has. */}
                  <StatusBadge variant={v.committed ? 'info' : 'neutral'}>
                    {v.committed ? t('vehicles.status.committed') : t('vehicles.status.available')}
                  </StatusBadge>
                </TableCell>
                <TableCell className="text-sm">
                  {/* Which auction holds it — the operator's next question after "İhalede".
                      Null when the API withholds the name: the auction is outside the caller's
                      own sale visibility, so the row says committed without saying where. */}
                  {v.committedSale ? (
                    can(state.roles, 'salesView') ? (
                      <Link
                        to="/sales/$saleId"
                        params={{ saleId: v.committedSale.id }}
                        className="text-tx-brand hover:underline"
                      >
                        {v.committedSale.title ?? v.committedSale.code}
                      </Link>
                    ) : (
                      <span>{v.committedSale.title ?? v.committedSale.code}</span>
                    )
                  ) : (
                    <span className="text-muted-foreground">{t('common.none')}</span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      ) : filtered ? (
        // A filtered empty result is not an empty pool — offering "New vehicle" here would
        // answer the wrong question.
        <EmptyState
          icon={Search}
          title={t('vehicles.emptyFiltered')}
          description={t('vehicles.emptyFilteredHint')}
          action={
            <Button variant="outline" size="sm" onClick={clearFilters}>
              {t('vehicles.filter.clear')}
            </Button>
          }
        />
      ) : (
        <EmptyState icon={Car} title={t('vehicles.empty')} action={newVehicleButton || undefined} />
      )}

      {list.data && (
        <Pagination offset={offset} limit={PAGE} total={list.data.total} onOffsetChange={goToPage} />
      )}
    </div>
  );
}




